import React from 'react';
import { Github, Linkedin, Twitter, Instagram, Facebook, Youtube, Twitch, MessageCircle, Slack, Dribbble, Figma, Globe, Mail, Phone, MapPin, Calendar, Link, ExternalLink, Code, Coffee, Heart, Star, Bookmark, Send, ArrowUp } from 'lucide-react';
import { usePortfolio } from '../context/PortfolioContext';

// Icon mapping - same as Contact.jsx for consistency
const iconMap = {
  Github, Linkedin, Twitter, Instagram, Facebook, Youtube, 
  Twitch, Discord: MessageCircle, Slack, Dribbble, Figma, Globe, 
  Mail, Phone, MapPin, Calendar, Link, ExternalLink,
  Code, Coffee, Heart, Star, Bookmark, Send
};

const Footer = () => {
  const { portfolioData, loading } = usePortfolio();
  const currentYear = new Date().getFullYear();
  
  if (loading || !portfolioData) {
    return null;
  }
  
  const { basics, socialLinks = [] } = portfolioData;

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="bg-zinc-950 border-t border-orange-500/10">
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
          {/* Brand */}
          <div className="space-y-4">
            <h3 className="text-3xl font-bold gradient-text">{basics.name}</h3>
            <p className="text-gray-400 text-lg">{basics.label}</p>
            <p className="text-gray-500 text-sm leading-relaxed">
              Crafting exceptional digital experiences with modern technologies.
            </p>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <h4 className="text-lg font-bold text-white">Quick Links</h4>
            <div className="flex flex-col gap-3">
              {['About', 'Skills', 'Experience', 'Awards', 'Contact'].map((link) => (
                <button
                  key={link}
                  onClick={() => {
                    const element = document.getElementById(link.toLowerCase());
                    if (element) element.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="text-gray-400 hover:text-orange-500 transition-colors text-left group flex items-center gap-2"
                >
                  <span className="text-orange-500 opacity-0 group-hover:opacity-100 transition-opacity">▸</span>
                  {link}
                </button>
              ))}
            </div>
          </div>

          {/* Social Links - Uses same socialLinks as Contact section */}
          <div className="space-y-4">
            <h4 className="text-lg font-bold text-white">Connect</h4>
            <div className="flex flex-wrap gap-4">
              {socialLinks && socialLinks.length > 0 ? (
                socialLinks
                  .sort((a, b) => (a.order || 0) - (b.order || 0))
                  .map((link, index) => {
                    const IconComponent = iconMap[link.icon] || Link;
                    return (
                      <a
                        key={link.id || index}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={link.tooltip || link.name}
                        className="p-4 glass rounded-xl hover:bg-orange-500/10 border border-orange-500/10 hover:border-orange-500/30 transition-all duration-300 group"
                      >
                        <IconComponent size={24} className="text-gray-400 group-hover:text-orange-500 transition-colors" />
                      </a>
                    );
                  })
              ) : (
                <p className="text-gray-500 text-sm">No social links configured</p>
              )}
            </div>
          </div>
        </div>

        <div className="border-t border-orange-500/10 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-sm">
              © {currentYear} {basics.name}. All rights reserved.
            </p>
            <p className="text-gray-500 text-sm flex items-center gap-2">
              Built with <Heart size={16} className="text-orange-500" /> using React & Tailwind CSS
            </p>
            <button
              onClick={scrollToTop}
              className="p-3 glass rounded-full hover:bg-orange-500/10 border border-orange-500/10 hover:border-orange-500/30 transition-all duration-300 group"
            >
              <ArrowUp size={20} className="text-gray-400 group-hover:text-orange-500 transition-colors" />
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
